import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSiteName } from "@/hooks/useSiteName";
import { ValidationError } from "@/types/problemDetails";
import type { LayoutContext } from "@/lib/models";
import { setFormErrors } from "@/lib/react-form-utils";
import { FeatureTypeService, type FeatureType, type  UpdateFeatureTypeRequest } from "@/services/FeatureTypeService";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useOutletContext, useParams } from "react-router";

const FeatureTypeEditPage: React.FC = () => {
  const params = useParams();
  const { siteName } = useSiteName(params.siteId);
  const { register, handleSubmit, formState: { isDirty, errors }, reset, setError } = useForm<UpdateFeatureTypeRequest>({defaultValues: {description: ""}});
  const { setPageName } = useOutletContext<LayoutContext>();
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [featureType, setFeatureType] = useState<FeatureType | null>(null);

  useEffect(() => {
    setIsSaveDisabled(!isDirty);
  }, [isDirty]);

  useEffect(() => {
    const initializeData = async () => {
      if (!params.featureTypeId) {
        return;
      }

      const featureType = await FeatureTypeService.getById(params.featureTypeId);
      setFeatureType(featureType);
      reset(featureType);
    };
    initializeData();
  }, [params.featureTypeId, reset]);

  useEffect(() => {
    setPageName(`Site: ${siteName} Create Feature Type`);
  }, [siteName, setPageName]);

  const handleFormSubmit = async (formData: UpdateFeatureTypeRequest) => {
    if (!params.siteId) {
      alert('error invalid siteId')
      return null
    }

    try {
        await FeatureTypeService.update(params.featureTypeId!, {description: formData.description});
        setIsSaveDisabled(true);
        reset({description: formData.description});
    } catch (error) {
        if (error instanceof ValidationError) {
          setFormErrors<UpdateFeatureTypeRequest>(error.errors, setError, formData)
        }
    }
    return null;
  };

  return (
    <div className="container mx-auto p-4">
      <Header headerTitle={`Site: ${siteName}`} />
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <div>
          <Label>
            Name
          </Label>
          <div>{featureType?.name}</div>
        </div>        
        <div>
          <Label
            htmlFor="description"
          >
            Description
          </Label>
          <Input
            type="text"
            {...register("description")}
          />
          <span className="text-sm text-red-500">{errors.description?.message}</span>
        </div>
        <div>
          <Label>
            Reference
          </Label>
          <div>{featureType?.reference}</div>
        </div>    
        <div className="flex justify-end space-x-2">
          <button
            type="reset"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <Button
            type="submit"
            disabled={isSaveDisabled}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Update Feature Type
          </Button>
        </div>
      </form>
    </div>
  );
};

export default FeatureTypeEditPage;
